import { Box, Typography } from '@mui/material';

import { SHRED_COLORS, SHRED_COPY, SHRED_FONTS, SHRED_ME_REASONS, SHRED_STATUS } from '../constants';
import {
  buildGhostCopy,
  buildNudgeText,
  formatPct,
  getGhostPosition,
  getQualificationNudge,
  getQualificationProgress,
  getSessionsToQualify,
  isRookie,
  pluralize,
} from '../helpers';
import { useNow } from '../hooks';
import { CountUp, Panel, QualifyRing, pctColor } from './shared';

const getHype = ({ entry, status, minSessions, totalRanked }) => {
  if (status === SHRED_STATUS.ENDED) {
    if (entry.qualified) {
      if (entry.rank <= 3) {
        return isRookie(entry) ? 'You finished top three in the Rookie bracket. Legendary.' : 'You finished on the podium. Legendary.';
      }
      return 'Shredded. You finished the contest.';
    }
    return 'The contest is over. Come back for the next one.';
  }
  if (!entry.qualified) {
    const need = getSessionsToQualify(entry, minSessions);
    return need === 1 ? 'One more session and you are on the board.' : `${pluralize(need, 'session')} to go and you are ranked.`;
  }
  if (isRookie(entry) && entry.rank <= 3) {
    return entry.rank === 1 ? 'You lead the Rookie bracket. Rookie Star is yours to lose.' : 'Top three in the Rookie bracket. Keep climbing.';
  }
  if (entry.rank === 1) return 'You are in front. Everyone is chasing you now.';
  if (entry.rank <= 3) return 'Podium position. Defend it.';
  if (entry.improvementPct > 0 && totalRanked > entry.rank) {
    return `You are beating your baseline. ${totalRanked - entry.rank} members are behind you.`;
  }
  if (entry.improvementPct > 0) return 'You are beating your baseline.';
  return 'Every session pulls your average up. Push your next one.';
};

// 0% is your ghost (your baseline self); the marker shows how far ahead or behind you are.
const GhostTrack = ({ pct, reduced }) => {
  const ghost = getGhostPosition(pct);
  const copy = buildGhostCopy(pct);
  return (
    <Box sx={{ mt: 1.75 }}>
      <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 10, letterSpacing: '0.14em', color: SHRED_COLORS.muted }}>
        {SHRED_COPY.ME_GHOST_TITLE.toUpperCase()}
      </Typography>
      <Box
        role="img"
        aria-label={copy}
        sx={{ position: 'relative', mt: 1.5, mb: 2.25, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.1)' }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: ghost.pct >= 0 ? '50%' : `${ghost.ratio * 100}%`,
            width: `${Math.abs(ghost.ratio - 0.5) * 100}%`,
            background: ghost.pct >= 0 ? SHRED_COLORS.heat : SHRED_COLORS.negative,
            borderRadius: 4,
            transition: reduced ? 'none' : 'all 700ms ease',
          }}
        />
        <Box aria-hidden="true" sx={{ position: 'absolute', left: '50%', top: -5, bottom: -5, width: 2, background: SHRED_COLORS.muted }} />
        <Box aria-hidden="true" sx={{ position: 'absolute', left: '50%', top: -22, transform: 'translateX(-50%)', fontSize: 15 }}>👻</Box>
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${ghost.ratio * 100}%`,
            width: 16,
            height: 16,
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            background: SHRED_COLORS.gold,
            border: `2px solid ${SHRED_COLORS.ink}`,
            boxShadow: `0 0 10px ${SHRED_COLORS.gold}`,
            transition: reduced ? 'none' : 'left 700ms ease',
          }}
        />
        <Typography
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 16,
            left: `${Math.min(88, Math.max(12, ghost.ratio * 100))}%`,
            transform: 'translateX(-50%)',
            fontFamily: SHRED_FONTS.numeric,
            fontSize: 11,
            color: SHRED_COLORS.gold,
            whiteSpace: 'nowrap',
          }}
        >
          {formatPct(ghost.pct)}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 13, color: SHRED_COLORS.text }}>{copy}</Typography>
    </Box>
  );
};

const MyProgress = ({ me, challenge, status, totalRanked, reduced, onJump, skewMs, domId = 'shred-me' }) => {
  const nowMs = useNow(30000, skewMs);
  if (!me) return null;

  if (!me.entry) {
    const rookieMe = me.eligible && me.reason === SHRED_ME_REASONS.ROOKIE;
    const copy =
      status === SHRED_STATUS.ENDED
        ? SHRED_COPY.ME_ENDED_NO_ENTRY
        : rookieMe
        ? SHRED_COPY.ME_ROOKIE(challenge.rookieBaselineSessions)
        : status === SHRED_STATUS.UPCOMING
        ? SHRED_COPY.ME_UPCOMING
        : me.reason === SHRED_ME_REASONS.NO_SESSIONS
        ? SHRED_COPY.ME_NO_SESSIONS
        : SHRED_COPY.ME_NO_BASELINE;
    return (
      <Panel id={domId} sx={{ p: 2, borderColor: SHRED_COLORS.line }} role="region" aria-label={SHRED_COPY.ME_TITLE}>
        <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 12, letterSpacing: '0.18em', color: SHRED_COLORS.flame }}>
          {SHRED_COPY.ME_TITLE.toUpperCase()}
        </Typography>
        <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 26, textTransform: 'uppercase', mt: 0.5 }}>
          {rookieMe ? SHRED_COPY.ME_ROOKIE_TITLE : me.eligible ? SHRED_COPY.ME_TITLE : SHRED_COPY.ME_INELIGIBLE_TITLE}
        </Typography>
        <Typography sx={{ color: SHRED_COLORS.muted, fontFamily: SHRED_FONTS.body, fontSize: 14, mt: 0.5 }}>{copy}</Typography>
      </Panel>
    );
  }

  const { entry } = me;
  const progress = getQualificationProgress(entry, challenge.minSessions);
  const tint = pctColor(entry.improvementPct);
  const rookie = isRookie(entry);
  const hasScore = !rookie || entry.sessions > challenge.rookieBaselineSessions;
  const nudge =
    status === SHRED_STATUS.LIVE ? getQualificationNudge({ entry, challenge, nowMs }) : null;
  return (
    <Panel
      id={domId}
      role="region"
      aria-label={SHRED_COPY.ME_TITLE}
      sx={{
        p: 2,
        border: `1px solid ${SHRED_COLORS.gold}55`,
        background: `linear-gradient(160deg, rgba(255,90,31,0.22), ${SHRED_COLORS.panel} 60%)`,
      }}
    >
      <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 12, letterSpacing: '0.18em', color: SHRED_COLORS.flame }}>
        {SHRED_COPY.ME_TITLE.toUpperCase()}
      </Typography>
      <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
        <QualifyRing done={progress.done} total={progress.total} size={64} label={`Qualifying sessions: ${progress.done} of ${progress.total}`} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontFamily: SHRED_FONTS.numeric,
              fontSize: 32,
              lineHeight: 1,
              color: tint,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <CountUp value={entry.improvementPct} format={(v) => formatPct(v)} reduced={reduced} />
          </Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 13, color: SHRED_COLORS.muted }}>
            {rookie ? `vs your first ${challenge.rookieBaselineSessions} sessions` : `vs your ${challenge.baselineDays}-day baseline`}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 44, lineHeight: 1, color: SHRED_COLORS.gold }}>
            {entry.qualified ? `#${entry.rank}` : '–'}
          </Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 10, letterSpacing: '0.14em', color: SHRED_COLORS.muted }}>
            {entry.qualified ? 'RANK' : 'UNRANKED'}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ mt: 1.5, fontFamily: SHRED_FONTS.body, fontWeight: 600, fontSize: 15 }}>
        {getHype({ entry, status, minSessions: challenge.minSessions, totalRanked })}
      </Typography>
      <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 13, color: SHRED_COLORS.muted }}>
        {pluralize(entry.sessions, 'session')} so far
        {!entry.qualified && status !== SHRED_STATUS.ENDED && !nudge
          ? ` · ${pluralize(getSessionsToQualify(entry, challenge.minSessions), 'more session')} to qualify`
          : ''}
      </Typography>
      {nudge && (
        <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 13, fontWeight: 600, color: nudge.reachable ? SHRED_COLORS.gold : SHRED_COLORS.muted }}>
          {buildNudgeText(nudge)}
        </Typography>
      )}
      {rookie && (
        <Typography sx={{ mt: 0.5, fontFamily: SHRED_FONTS.body, fontSize: 12, color: '#d9bcff' }}>
          {SHRED_COPY.ME_ROOKIE(challenge.rookieBaselineSessions)}
        </Typography>
      )}
      {hasScore && <GhostTrack pct={entry.improvementPct} reduced={reduced} />}
      {onJump && (
        <Box
          component="button"
          type="button"
          onClick={onJump}
          sx={{
            mt: 1.25,
            p: 0,
            border: 0,
            background: 'none',
            color: SHRED_COLORS.gold,
            fontFamily: SHRED_FONTS.body,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            textDecoration: 'underline',
            '&:focus-visible': { outline: `2px solid ${SHRED_COLORS.gold}`, outlineOffset: 2 },
          }}
        >
          Find me on the board
        </Box>
      )}
    </Panel>
  );
};

export default MyProgress;
